import type { CSSProperties } from 'react';
import { Icon } from '../icons/Icon';
import { Button } from './Button';
import { errorDetails, resolveErrorContent } from './errorContent';
import { toast } from './Toast';
import styles from './ErrorView.module.css';

export interface ErrorViewProps {
  /** AppError-like object, AbsError, Error or message string. */
  error?: unknown;
  title?: string | null;
  message?: string | null;
  onRetry?: () => void;
  onRelogin?: () => void;
  compact?: boolean;
  /** Outer padding (default 0 24px 120px, Flutter LTRB(24, 0, 24, 120)). */
  padding?: CSSProperties['padding'];
  className?: string;
}

/**
 * ErrorView card: red triangle, title, message, Retry / Sign in again (spec 01
 * §10.6). Polish pass: the EmptyState rhythm (16 px icon to title, 8 px to the
 * message at paragraph line height, 24 px to the actions, text within 320 px).
 */
export function ErrorView({ error, title, message, onRetry, onRelogin, compact = false, padding = '0 24px 120px', className }: ErrorViewProps) {
  const content = resolveErrorContent({ error, title, message, hasRetry: Boolean(onRetry), hasRelogin: Boolean(onRelogin) });
  const showCopy = import.meta.env.DEV && error != null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(errorDetails(error));
      toast.info('Error details copied');
    } catch {
      toast.error('Could not copy error details');
    }
  };
  return (
    <div className={[styles.root, className ?? ''].filter(Boolean).join(' ')} style={{ padding }}>
      <div className={[styles.card, compact ? styles.compact : ''].filter(Boolean).join(' ')} role="alert">
        <Icon name="exclamationmark_triangle" size={compact ? 28 : 40} className={styles.icon} />
        <h2 className={styles.title}>{content.title}</h2>
        <p className={styles.message}>{content.message}</p>
        {(content.showRetry || content.showRelogin || showCopy) && (
          <div className={styles.actions}>
            {content.showRetry && (
              <Button variant="elevated" onClick={onRetry}>
                Retry
              </Button>
            )}
            {content.showRelogin && (
              <Button variant="outlined" onClick={onRelogin}>
                Sign in again
              </Button>
            )}
            {showCopy && (
              <Button variant="outlined" onClick={() => void copy()}>
                Copy details
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
